/**
 * @description ResetPassword component for completing password reset
 *
 * Key features:
 * - New password form with confirmation and strength indicators
 * - Reads code from URL params (supports both legacy and branded email flows)
 * - Calls POST /api/auth/reset-password with either emailAuthCode or passwordResetCode
 * - Redirects to sign in on success (or auto-login via PKCE if supported)
 * - Dark auth-card styling aligned with SignIn / ForgotPassword (brand tokens)
 *
 * @dependencies
 * - AuthLayout: Shared auth page layout
 * - apiClient: API calls to backend
 * - authTenantQuery: SO slug + minted wristbandTenant hop allowlist
 *
 * @notes
 * - passwordResetCode: From branded email via PasswordResetEmailService (self-hosted flow)
 * - emailAuthCode/code: From legacy Wristband-sent email (backwards compatible)
 * - Same password strength requirements as AcceptInvite
 * - Backend POST /api/auth/reset-password already handles both code types
 * - CP admin./app. doors follow the registered api /router/login hop (LYN-335)
 * - Passes minted `wristbandTenant` into shouldFollowAuthRedirect so only that
 *   Wristband vanity is followed. LynxPay / platform hops are always refused.
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import apiClient from '../../services/api/apiClient';
import AuthLayout from './AuthLayout';
import { useBranding } from '../../contexts/BrandingContext';
import { resolveAuthSubdomain, shouldFollowAuthRedirect, withAuthSubdomain } from '../../utils/authTenantQuery';

/**
 * Password strength requirements
 */
const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lowercase', label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'One number', test: (pw) => /[0-9]/.test(pw) },
];

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const { slug: brandingSlug } = useBranding();

  // Get code from URL params — supports both forgot-password flows:
  // - emailAuthCode: Legacy Wristband-sent email (email_auth_code or code param)
  // - passwordResetCode: Branded email via PasswordResetEmailService
  const emailAuthCode = searchParams.get('email_auth_code') || searchParams.get('code');
  const passwordResetCode = searchParams.get('passwordResetCode');
  const mintedWristbandTenant = searchParams.get('wristbandTenant');
  const hasValidCode = !!(emailAuthCode || passwordResetCode);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);

  // Check for valid code on mount
  useEffect(() => {
    if (!hasValidCode) {
      setError('Invalid or missing reset link. Please request a new password reset.');
    }
  }, [hasValidCode]);

  /**
   * Check password strength
   */
  const checkPasswordStrength = (pw) => {
    const passed = PASSWORD_REQUIREMENTS.filter(req => req.test(pw));
    return {
      passed,
      isStrong: passed.length === PASSWORD_REQUIREMENTS.length
    };
  };

  const passwordStrength = checkPasswordStrength(password);

  /**
   * Validate form fields
   */
  const validateForm = () => {
    const errors = {};

    if (!password) {
      errors.password = 'Password is required';
    } else if (!passwordStrength.isStrong) {
      errors.password = 'Password does not meet requirements';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle input change and clear field error
   */
  const handleInputChange = (field, value) => {
    if (field === 'password') {
      setPassword(value);
    } else if (field === 'confirmPassword') {
      setConfirmPassword(value);
    }

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }

    // Clear general error
    if (error) {
      setError(null);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasValidCode) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Build request body based on code type
      const body = { newPassword: password };
      if (passwordResetCode) {
        body.passwordResetCode = passwordResetCode;
      } else {
        body.emailAuthCode = emailAuthCode;
      }

      const subdomain = await resolveAuthSubdomain({ brandingSlug, searchParams });
      const resetUrl = withAuthSubdomain('/auth/reset-password', subdomain);

      const response = await apiClient.post(resetUrl, body);

      if (response.data.success) {
        // Check if auto-login is available on this origin
        if (response.data.redirectUrl
          && shouldFollowAuthRedirect(response.data.redirectUrl, undefined, mintedWristbandTenant)) {
          // Follow redirect for auto-login via OAuth flow
          window.location.href = response.data.redirectUrl;
        } else {
          // Show success and redirect to sign in
          setIsSuccess(true);
        }
      } else {
        setError('Unexpected response from server. Please try again.');
      }

    } catch (err) {
      console.error('Reset password error:', err);

      // Handle specific error codes
      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;

      if (errorCode === 'AUTH_INVALID_CODE' || errorCode === 'AUTH_CODE_EXPIRED') {
        setError('This reset link has expired or is invalid. Please request a new password reset.');
      } else if (errorCode === 'AUTH_WEAK_PASSWORD') {
        setError('Password does not meet security requirements.');
      } else if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else if (errorMessage) {
        setError(errorMessage);
      } else {
        setError('Unable to reset password. Please try again or request a new reset link.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success state
  if (isSuccess) {
    return (
      <AuthLayout helper={null}>
        <div className="flex flex-col items-center text-center py-8">
          <div className="w-16 h-16 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center mb-6">
            <CheckCircle className="w-8 h-8 text-brand" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Password reset successful
          </h1>
          <p className="text-gray-400 mb-8 text-sm">
            Your password has been changed. You can now sign in with your new password.
          </p>
          <Link
            to="/sign-in"
            className="w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] text-center shadow-2xl"
          >
            Sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Show invalid code state
  if (!hasValidCode) {
    return (
      <AuthLayout helper={null}>
        <div className="flex flex-col items-center text-center py-8">
          <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Invalid reset link
          </h1>
          <p className="text-gray-400 mb-8 text-sm">
            This password reset link is invalid or has expired.
            Please request a new one.
          </p>
          <div className="space-y-3 w-full">
            <Link
              to="/forgot-password"
              className="block w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] text-center shadow-2xl"
            >
              Request new reset link
            </Link>
            <Link
              to="/sign-in"
              className="block w-full text-center text-sm font-bold text-brand hover:underline underline-offset-4 transition-all"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout helper={null}>
      <div className="space-y-8">
        <div>
          <Link
            to="/sign-in"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-brand hover:text-brand-hover transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>
          <div className="text-center mt-6">
            <h1 className="text-3xl font-bold tracking-tight text-white mb-2">Set new password</h1>
            <p className="text-gray-500 text-sm font-medium">Enter your new password below</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-300 transition-colors"
                aria-label="Dismiss error"
              >
                <span className="text-lg leading-none">&times;</span>
              </button>
            </div>
          )}

          <div className="space-y-1.5">
            <label htmlFor="password" className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] ml-1">
              New password
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={`w-full bg-white/[0.03] border rounded-2xl px-4 py-3.5 pr-12 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:ring-4 outline-none ${
                  fieldErrors.password
                    ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/10'
                    : 'border-white/10 focus:border-brand/50 focus:ring-brand/10'
                }`}
                placeholder="••••••••"
                disabled={isSubmitting}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {fieldErrors.password && (
              <p className="text-xs text-red-400 ml-1">{fieldErrors.password}</p>
            )}

            {password && (
              <div className="mt-2 space-y-1">
                {PASSWORD_REQUIREMENTS.map(req => {
                  const passed = req.test(password);
                  return (
                    <div
                      key={req.id}
                      className={`flex items-center gap-2 text-xs ${
                        passed ? 'text-brand' : 'text-gray-500'
                      }`}
                    >
                      <CheckCircle className={`h-3.5 w-3.5 ${passed ? 'opacity-100' : 'opacity-40'}`} />
                      {req.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="confirmPassword" className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] ml-1">
              Confirm new password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className={`w-full bg-white/[0.03] border rounded-2xl px-4 py-3.5 pr-12 text-white placeholder-gray-600 transition-all focus:bg-white/[0.06] focus:ring-4 outline-none ${
                  fieldErrors.confirmPassword
                    ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/10'
                    : 'border-white/10 focus:border-brand/50 focus:ring-brand/10'
                }`}
                placeholder="••••••••"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {fieldErrors.confirmPassword && (
              <p className="text-xs text-red-400 ml-1">{fieldErrors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-brand text-[#0a0a0b] font-bold rounded-2xl px-4 py-4 text-sm tracking-tight transition-all hover:bg-brand-hover active:scale-[0.98] disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2 shadow-2xl"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                Resetting password...
              </>
            ) : (
              'Reset password'
            )}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}

export default ResetPassword;
